import { ROUNDNESS } from "../constants";
import { ElementOrToolType } from "../types";
import { MarkNonNullable } from "../utility-types";
import { assertNever } from "../utils";
import {
  ExcalidrawElement,
  ExcalidrawTextElement,
  ExcalidrawEmbeddableElement,
  ExcalidrawLinearElement,
  ExcalidrawBindableElement,
  ExcalidrawFreeDrawElement,
  InitializedExcalidrawImageElement,
  ExcalidrawImageElement,
  ExcalidrawTextElementWithContainer,
  ExcalidrawTextContainer,
  ExcalidrawFrameElement,
  RoundnessType,
  ExcalidrawFrameLikeElement,
  ExcalidrawElementType,
  ExcalidrawIframeElement,
  ExcalidrawIframeLikeElement,
  ExcalidrawMagicFrameElement,
  ExcalidrawMindmapElement, ExcalidrawStickyNotesElement,
} from "./types";
import {
  isAnnoElement,
  isAudioAnnoElement,
  isFileElement,
  isFileVideoElement,
  isNoteElement
} from "../../../../src/utils";
import {HAS_ENABLE_TIPTAP_TEXT} from "../../../../src/Const";

export const isInitializedImageElement = (
    element: ExcalidrawElement | null,
): element is InitializedExcalidrawImageElement => {
  return !!element && element.type === "image" && !!element.fileId;
};

export const isImageElement = (
    element: ExcalidrawElement | null,
): element is ExcalidrawImageElement => {
  return !!element && element.type === "image";
};

export const isShapeElement = (element: ExcalidrawElement | null)=>{
  return element && (element.type == 'rectangle' || element.type == 'roundedRectangle' || element.type == 'ellipse' || element.type == 'triangle' || element.type == 'diamond' || element.type == 'bubble' || element.type == 'parallelogram' || element.type == 'pentagram' || element.type == 'thickArrow');
}

export const isEmbeddableElement = (
    element: ExcalidrawElement | null | undefined,
): element is ExcalidrawEmbeddableElement => {
  return !!element && element.type === "embeddable";
};

export const isMindmapElement = (
    element: ExcalidrawElement | null | undefined,
): element is ExcalidrawMindmapElement => {
  return !!element && element.type === "mindmap";
};

export const isEmbedableBookmarkElement = (
    element: ExcalidrawElement | null | undefined,
): element is ExcalidrawMindmapElement => {
  return !!element && element.type === "embeddable" && element.embedType == 'text';
};

export const isStickyNotesElement = (
    element: ExcalidrawElement | null | undefined,
): element is ExcalidrawStickyNotesElement => {
  return !!element && element.type === "stickyNotes";
};

export const isProjectLinkElement = (
    element: ExcalidrawElement | null | undefined,
): element is ExcalidrawMindmapElement => {
  return !!element && element.type === "projectLink";
};

export const isIframeElement = (
    element: ExcalidrawElement | null,
): element is ExcalidrawIframeElement => {
  return !!element && element.type === "iframe";
};

export const isIframeLikeElement = (
    element: ExcalidrawElement | null,
): element is ExcalidrawIframeLikeElement => {
  return (
      !!element && (element.type === "iframe" || element.type === "embeddable")
  );
};

export const isTextElement = (
    element: ExcalidrawElement | null,
): element is ExcalidrawTextElement => {
  return element != null && element.type === "text";
};

export const isTextOrBoundElement = (
    element: ExcalidrawElement | null,
): element is ExcalidrawTextElement => {
  let isBoundText = false;
  if (element?.boundElements != null && element.boundElements != undefined && element.boundElements.length>0){
    if (element.boundElements[0].type === "text"){
      isBoundText = true;
    }
  }
  return element != null && (element.type === "text" || isBoundText);
};

export const isBoundElementText = (
    element: ExcalidrawElement | null,
): element is ExcalidrawTextElement => {
  let isBoundText = false;
  if (element?.boundElements != null && element.boundElements != undefined && element.boundElements.length>0){
    if (element.boundElements[0].type === "text"){
      isBoundText = true;
    }
  }
  return isBoundText;
};

export const isFrameElement = (
    element: ExcalidrawElement | null,
): element is ExcalidrawFrameElement => {
  return element != null && element.type === "frame";
};

export const isMagicFrameElement = (
    element: ExcalidrawElement | null,
): element is ExcalidrawMagicFrameElement => {
  return element != null && element.type === "magicframe";
};

export const isFrameLikeElement = (
    element: ExcalidrawElement | null,
): element is ExcalidrawFrameLikeElement => {
  return (
      element != null &&
      (element.type === "frame" || element.type === "magicframe")
  );
};

export const isFreeDrawElement = (
    element?: ExcalidrawElement | null,
): element is ExcalidrawFreeDrawElement => {
  return element != null && isFreeDrawElementType(element.type);
};

export const isFreeDrawElementType = (
    elementType: ExcalidrawElementType,
): boolean => {
  return elementType === "freedraw" || elementType === "highlighter";
};

export const isLinearElement = (
    element?: ExcalidrawElement | null,
): element is ExcalidrawLinearElement => {
  return element != null && isLinearElementType(element.type);
};

export const isArrowElement = (
    element?: ExcalidrawElement | null,
): element is ExcalidrawLinearElement => {
  return element != null && element.type === "arrow";
};

export const isLinearElementType = (
    elementType: ElementOrToolType,
): boolean => {
  return (
      elementType === "arrow" || elementType === "line" // || elementType === "freedraw"
  );
};

export const isBindingElement = (
    element?: ExcalidrawElement | null,
    includeLocked = true,
): element is ExcalidrawLinearElement => {
  return (
      element != null &&
      (!element.locked || includeLocked === true) &&
      isBindingElementType(element.type)
  );
};

export const isBindingElementType = (
    elementType: ElementOrToolType,
): boolean => {
  return elementType === "arrow";
};

export const isBindableElement = (
    element: ExcalidrawElement | null,
    includeLocked = true,
): element is ExcalidrawBindableElement => {
  return (
      element != null &&
      (!element.locked || includeLocked === true) &&
      (element.type === "rectangle" ||
          element.type === "roundedRectangle" ||
          element.type === "ellipse" ||
          element.type === "triangle" ||
          element.type === "diamond" ||
          element.type === "bubble" ||
          element.type === "parallelogram" ||
          element.type === "pentagram" ||
          element.type === "thickArrow" ||
          element.type === "image" ||
          element.type === "iframe" ||
          element.type === "embeddable" ||
          element.type === "stickyNotes" ||
          element.type === "mindmap" ||
          element.type === "frame" ||
          element.type === "magicframe" ||
          element.type === "projectLink" ||
          (element.type === "text" && !element.containerId))
  );
};

export const isHoverAbleElement = (
    element: ExcalidrawElement | null,
    includeLocked = true,
): element is ExcalidrawTextContainer => {
  return (
      element != null &&
      (!element.locked || includeLocked === true) &&
      (element.type === "rectangle" ||
          element.type === "roundedRectangle" ||
          element.type === "ellipse" ||
          element.type === "triangle" ||
          element.type === "diamond" ||
          element.type === "bubble" ||
          element.type === "parallelogram" ||
          element.type === "pentagram" ||
          element.type === "thickArrow" ||
          element.type === "stickyNotes" || (isImageElement(element) && !element.extra?.svg) || isFileElement(element) || isEmbeddableElement(element) ||
      isNoteElement(element) || isAnnoElement(element) || isProjectLinkElement(element) || isTextElement(element))
  );
};

export const isTextBindableContainer = (
    element: ExcalidrawElement | null,
    includeLocked = true,
): element is ExcalidrawTextContainer => {
  return (
      element != null &&
      (!element.locked || includeLocked === true) &&
      (element.type === "rectangle" ||
          element.type === "roundedRectangle" ||
          element.type === "ellipse" ||
          element.type === "triangle" ||
          element.type === "diamond" ||
          element.type === "bubble" ||
          element.type === "parallelogram" ||
          element.type === "pentagram" ||
          element.type === "thickArrow" ||
          isArrowElement(element))
  );
};

export const isExcalidrawElement = (
    element: any,
): element is ExcalidrawElement => {
  const type: ExcalidrawElementType | undefined = element?.type;
  if (!type) {
    return false;
  }
  switch (type) {
    case "text":
    case "rectangle":
    case "roundedRectangle":
    case "ellipse":
    case "triangle":
    case "diamond":
    case "bubble":
    case "parallelogram":
    case "pentagram":
    case "thickArrow":
    case "iframe":
    case "embeddable":
    case "mindmap":
    case "arrow":
    case "freedraw":
    case "highlighter":
    case "line":
    case "frame":
    case "magicframe":
    case "image":
    case "stickyNotes":
    case "projectLink":
    case "selection": {
      return true;
    }
    default: {
      assertNever(type, null);
      return false;
    }
  }
};

export const hasBoundTextElement = (
    element: ExcalidrawElement | null,
): element is MarkNonNullable<ExcalidrawBindableElement, "boundElements"> => {
  return (
      isTextBindableContainer(element) &&
      !!element.boundElements?.some(({ type }) => type === "text")
  );
};

export const isBoundToContainer = (
    element: ExcalidrawElement | null,
): element is ExcalidrawTextElementWithContainer => {
  return (
      element !== null &&
      "containerId" in element &&
      element.containerId !== null && element.containerId != undefined && element.containerId != ''
  );
};

export const isBoundToContainer0 = (
    element: ExcalidrawElement | null,
): element is ExcalidrawTextElementWithContainer => {
  return (
      element !== null &&
      "containerId" in element &&
      element.containerId !== null && element.containerId != undefined && element.containerId != ''
  );
};


// 只有矩形是变border，单独区分
export const isUsingAdaptiveRadius = (type: string) =>
    type === "rectangle" ||
    type === "roundedRectangle" ||
    type === "embeddable" ||
    type === "mindmap" ||
    type === "iframe";

export const isUsingProportionalRadius = (type: string) =>
    type === "line" || type === "arrow" || type === "diamond";

export const canApplyRoundnessTypeToElement = (
    roundnessType: RoundnessType,
    element: ExcalidrawElement,
) => {
  if (
      (roundnessType === ROUNDNESS.ADAPTIVE_RADIUS ||
          // if legacy roundness, it can be applied to elements that currently
          // use adaptive radius
          roundnessType === ROUNDNESS.LEGACY) &&
      isUsingAdaptiveRadius(element.type)
  ) {
    return true;
  }
  if (
      roundnessType === ROUNDNESS.PROPORTIONAL_RADIUS &&
      isUsingProportionalRadius(element.type)
  ) {
    return true;
  }

  return false;
};

export const getDefaultRoundnessTypeForElement = (
    element: ExcalidrawElement,
) => {
  if (isUsingProportionalRadius(element.type)) {
    return {
      type: ROUNDNESS.PROPORTIONAL_RADIUS,
    };
  }

  if (isUsingAdaptiveRadius(element.type)) {
    return {
      type: ROUNDNESS.ADAPTIVE_RADIUS,
    };
  }

  return null;
};
